import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const TAG_COLORS = [
  "#E8A030", "#7AB8A0", "#A08CC8", "#C86F3C", "#6AABB8",
  "#E87070", "#4A7A5A", "#8C7A5A", "#D4A574", "#6A8AB8",
];

const getTagColor = (tag) => TAG_COLORS[Math.abs([...tag].reduce((h, c) => ((h << 5) - h) + c.charCodeAt(0), 0)) % TAG_COLORS.length];

const HighlightText = ({ text, query }) => {
  if (!query || !text) return <>{text}</>;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="bg-[rgba(232,160,48,0.35)] text-[#FDF8EE] px-0.5 rounded-sm">{part}</mark>
          : part
      )}
    </>
  );
};

export const BookmarkCard = ({
  bookmark, index, bulkMode, selectedBookmarks, toggleBulkSelect,
  getCollectionColor, getCollectionName, getImageUrl,
  copyToClipboard, copiedId, setEditingBookmark, setBookmarkForm,
  setBookmarkModalOpen, setViewingBookmark, fetchRelated,
  handleDeleteBookmark, handleTogglePin, onTagClick, recordView, searchQuery,
}) => (
  <div
    className={`card-coke p-4 border-l-[3px] animate-fade-up group ${bookmark.pinned ? 'ring-1 ring-[rgba(232,160,48,0.2)]' : ''}`}
    style={{ borderLeftColor: getCollectionColor(bookmark.collection_id), animationDelay: `${index * 30}ms` }}
    data-testid={`bookmark-${bookmark.bookmark_id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-2">
        {bulkMode && (
          <input
            type="checkbox"
            checked={selectedBookmarks.has(bookmark.bookmark_id)}
            onChange={() => toggleBulkSelect(bookmark.bookmark_id)}
            className="w-4 h-4 accent-[#E8A030] cursor-pointer"
            data-testid={`bulk-check-${bookmark.bookmark_id}`}
          />
        )}
        {bookmark.pinned && <span className="text-[#E8A030] text-xs" title="Pinned">&#9650;</span>}
        <span style={{ color: getCollectionColor(bookmark.collection_id) }}>◈</span>
        <span className="font-mono text-[8px] text-[#4A3A1A] uppercase tracking-[1px]">{getCollectionName(bookmark.collection_id)}</span>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 text-[#4A3A1A] hover:text-[#E8A030] transition-colors" data-testid={`bookmark-menu-${bookmark.bookmark_id}`}>⋮</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#1C1004] border-[rgba(232,160,48,0.15)] text-[#F5ECD0]">
          <DropdownMenuItem onClick={() => handleTogglePin?.(bookmark.bookmark_id)} className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]" data-testid={`pin-${bookmark.bookmark_id}`}>
            {bookmark.pinned ? "Unpin" : "Pin to Top"}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => { recordView?.(bookmark.bookmark_id); copyToClipboard(bookmark.message_content, bookmark.bookmark_id); }} className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]">
            {copiedId === bookmark.bookmark_id ? "✓ Copied" : "Copy Content"}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => { recordView?.(bookmark.bookmark_id); setEditingBookmark(bookmark); setBookmarkForm({ collection_id: bookmark.collection_id || "", annotation: bookmark.annotation || "", tags: bookmark.tags || [] }); setBookmarkModalOpen(true); }}
            className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]"
            data-testid={`bookmark-edit-${bookmark.bookmark_id}`}
          >Edit</DropdownMenuItem>
          {bookmark.source_url && (
            <DropdownMenuItem asChild>
              <a href={bookmark.source_url} target="_blank" rel="noopener noreferrer" className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]">View Source</a>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => { recordView?.(bookmark.bookmark_id); setViewingBookmark(bookmark); fetchRelated(bookmark.bookmark_id); }} className="text-[#8C7A5A] hover:text-[#FDF8EE] hover:bg-[rgba(232,160,48,0.1)]">Related</DropdownMenuItem>
          <DropdownMenuSeparator className="bg-[rgba(232,160,48,0.1)]" />
          <DropdownMenuItem onClick={() => handleDeleteBookmark(bookmark.bookmark_id)} className="text-red-400 hover:text-red-300 hover:bg-[rgba(255,0,0,0.1)]" data-testid={`bookmark-delete-${bookmark.bookmark_id}`}>Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>

    {bookmark.annotation && (
      <div className="bg-[rgba(200,96,10,0.1)] border-l-2 border-[#C8600A] px-3 py-2 mb-3">
        <p className="text-sm text-[#F5ECD0] font-medium"><HighlightText text={bookmark.annotation} query={searchQuery} /></p>
      </div>
    )}

    {bookmark.images?.length > 0 && (
      <div className="flex gap-2 mb-3 overflow-x-auto">
        {bookmark.images.map((img) => (
          <div key={img.image_id} className="relative shrink-0 group">
            <img src={getImageUrl(img.storage_path)} alt={img.original_filename} className="h-16 w-auto object-cover border border-[rgba(232,160,48,0.1)]" data-testid={`card-image-${img.image_id}`} />
            {img.ocr_text && (
              <div className="absolute top-0.5 right-0.5 w-3 h-3 bg-[#E8A030] flex items-center justify-center" title="Contains extracted text">
                <span className="text-[6px] text-[#0A0600] font-bold">T</span>
              </div>
            )}
          </div>
        ))}
      </div>
    )}

    <p className="text-[13px] text-[#8C7A5A] line-clamp-3 mb-3 leading-[1.6]"><HighlightText text={bookmark.message_content} query={searchQuery} /></p>

    {/* Tags */}
    {bookmark.tags?.length > 0 && (
      <div className="flex flex-wrap gap-1.5 mb-3">
        {bookmark.tags.map(tag => (
          <button
            key={tag}
            onClick={() => onTagClick?.(tag)}
            className="flex items-center gap-1 px-2 py-0.5 border transition-colors hover:opacity-80"
            style={{ borderColor: `${getTagColor(tag)}30`, backgroundColor: `${getTagColor(tag)}0A` }}
            data-testid={`tag-pill-${tag}`}
          >
            <span className="text-[9px] font-mono" style={{ color: getTagColor(tag) }}>#</span>
            <span className="text-[9px] text-[#F5ECD0] font-mono">{tag}</span>
          </button>
        ))}
      </div>
    )}

    <div className="flex items-center justify-between font-mono text-[9px]">
      <span className={`tag ${bookmark.message_role === 'assistant' ? 'tag-pro' : ''}`}>{bookmark.message_role}</span>
      <span className="text-[#4A3A1A]">{new Date(bookmark.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
    </div>
  </div>
);
